import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { CALL_OUTCOMES } from "@/lib/i18n";
import {
  Plus, X, Trash2, Phone, Clock, CalendarClock, PhoneOff, CheckCircle2,
  History, ListChecks, PlayCircle, AlertCircle, ChevronRight, User2, Pencil,
} from "lucide-react";
import CallButton from "@/components/CallButton";
import ContactTimeline from "@/components/ContactTimeline";
import StatusBadge from "@/components/StatusBadge";

const OUTCOME_STYLES = {
  no_answer: { bg: "bg-slate-100", text: "text-slate-700", icon: PhoneOff },
  callback_requested: { bg: "bg-amber-100", text: "text-amber-800", icon: CalendarClock },
  interested: { bg: "bg-blue-100", text: "text-blue-800", icon: CheckCircle2 },
  not_interested: { bg: "bg-red-100", text: "text-red-700", icon: PhoneOff },
  meeting_scheduled: { bg: "bg-emerald-100", text: "text-emerald-800", icon: CheckCircle2 },
  proposal_requested: { bg: "bg-cyan-100", text: "text-cyan-800", icon: CheckCircle2 },
  deal_won: { bg: "bg-emerald-200", text: "text-emerald-900", icon: CheckCircle2 },
  deal_lost: { bg: "bg-red-100", text: "text-red-700", icon: PhoneOff },
};

const TABS = [
  { id: "calling_list", labelEl: "Λίστα κλήσεων", labelEn: "Calling list", icon: ListChecks },
  { id: "today", labelEl: "Σήμερα", labelEn: "Today", icon: PlayCircle },
  { id: "queue", labelEl: "Ακάλεστοι", labelEn: "To Call", icon: ListChecks },
  { id: "history", labelEl: "Ιστορικό", labelEn: "History", icon: History },
];

export default function Calls() {
  const { t, lang } = useI18n();
  const [tab, setTab] = useState("calling_list");
  const [calls, setCalls] = useState([]);
  const [today, setToday] = useState([]);
  const [uncalled, setUncalled] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [callingList, setCallingList] = useState([]);
  const [openLog, setOpenLog] = useState(false);
  const [preset, setPreset] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineContactId, setTimelineContactId] = useState(null);
  const [editCall, setEditCall] = useState(null);

  const L = lang === "el";

  const load = useCallback(async () => {
    try {
      const [c, td, un, cs, cl] = await Promise.all([
        api.get("/calls"),
        api.get("/calls/today"),
        api.get("/calls/uncalled-contacts"),
        api.get("/contacts"),
        api.get("/calling-list"),
      ]);
      setCalls(c.data);
      setToday(td.data);
      setUncalled(un.data);
      setContacts(cs.data);
      setCallingList(cl.data);
    } catch { /* handled */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openLogFor = (contactId, scheduledCall = null) => {
    setPreset({ contact_id: contactId, existing_call_id: scheduledCall?.id });
    setOpenLog(true);
  };

  const showTimeline = async (contactId) => {
    const { data } = await api.get(`/contacts/${contactId}/timeline`);
    setTimeline(data);
  };

  const stats = {
    today: today.length,
    queue: uncalled.length,
    total: calls.length,
    completed: calls.filter((c) => c.status === "completed").length,
    planned: calls.filter((c) => c.status === "planned").length,
  };
  const contactMap = Object.fromEntries(contacts.map((c) => [c.id, c]));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("nav.calls")}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">
            {L ? "CRM Κλήσεων" : "Calls CRM"}
          </h1>
        </div>
        <button
          data-testid="call-create-button"
          onClick={() => { setPreset(null); setOpenLog(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold hover:bg-[#0043A8]"
        >
          <Phone className="w-4 h-4" /> {L ? "Νέα κλήση" : "Log call"}
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        <KpiTile icon={CalendarClock} label={L ? "Σήμερα" : "Today"} value={stats.today} accent="#F59E0B" testid="calls-kpi-today" />
        <KpiTile icon={ListChecks} label={L ? "Ακάλεστοι" : "To call"} value={stats.queue} accent="#0052CC" testid="calls-kpi-queue" />
        <KpiTile icon={Phone} label={L ? "Σύνολο" : "Total"} value={stats.total} accent="#8B5CF6" testid="calls-kpi-total" />
        <KpiTile icon={CheckCircle2} label={L ? "Ολοκληρώθηκαν" : "Completed"} value={stats.completed} accent="#10B981" testid="calls-kpi-completed" />
        <KpiTile icon={Clock} label={L ? "Προγραμματισμένες" : "Planned"} value={stats.planned} accent="#06B6D4" testid="calls-kpi-planned" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((T) => (
          <button
            key={T.id}
            data-testid={`calls-tab-${T.id}`}
            onClick={() => setTab(T.id)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              tab === T.id ? "border-[#0052CC] text-[#0052CC]" : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            <T.icon className="w-4 h-4" />
            {L ? T.labelEl : T.labelEn}
            <span className="text-xs opacity-70">({T.id === "calling_list" ? callingList.length : T.id === "today" ? stats.today : T.id === "queue" ? stats.queue : stats.total})</span>
          </button>
        ))}
      </div>

      {/* Calling list */}
      {tab === "calling_list" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="calling-list-table">
          {callingList.length === 0 ? (
            <EmptyState icon={ListChecks} text={L ? "Δεν υπάρχει λίστα κλήσεων. Επιλέξτε επαφές στη σελίδα 'Επαφές & Εταιρείες' με το tickbox." : "No calling list. Add customers via the 'Customers' page using the tickbox."} />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {[L ? "Εταιρεία" : "Company", L ? "Επαφή" : "Contact", L ? "Τηλέφωνο" : "Phone", L ? "Χώρα" : "Country", L ? "Κατάσταση" : "Status", L ? "Επόμενη ενέργεια" : "Next action", L ? "Ενέργειες" : "Actions"].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {callingList.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`calling-list-row-${c.id}`}>
                    <td className="py-3 px-4 font-semibold text-slate-900">{c.company_name || "—"}</td>
                    <td className="py-3 px-4 text-slate-700">
                      <div>{c.first_name} {c.last_name}</div>
                      {c.position && <div className="text-xs text-slate-500">{c.position}</div>}
                    </td>
                    <td className="py-3 px-4 text-slate-700 text-xs whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {c.phone || "—"}
                        {c.phone && <CallButton phone={c.phone} contactId={c.id} size="xs" />}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-700 text-xs">{c.country_code || c.country || "—"}</td>
                    <td className="py-3 px-4"><StatusBadge status={c.pipeline_status || "new"} /></td>
                    <td className="py-3 px-4 text-xs text-slate-600">
                      {c.next_action_at ? new Date(c.next_action_at).toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <button data-testid={`calling-history-${c.id}`} onClick={() => setTimelineContactId(c.id)} className="p-1.5 text-slate-500 hover:text-[#0052CC]" title={L ? "Ιστορικό" : "History"}><History className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Today's calls */}
      {tab === "today" && (
        <div className="space-y-3">
          {today.length === 0 ? (
            <EmptyState icon={CalendarClock} text={L ? "Δεν υπάρχουν προγραμματισμένες κλήσεις για σήμερα." : "No planned calls for today."} />
          ) : today.map((c) => {
            const contact = contactMap[c.contact_id];
            const O = OUTCOME_STYLES[c.outcome] || OUTCOME_STYLES.no_answer;
            return (
              <div key={c.id} data-testid={`today-call-${c.id}`} className="bg-white border border-slate-200 rounded-lg p-5 flex items-center gap-4 hover:shadow-sm transition-all">
                <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <CalendarClock className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-slate-900 truncate">
                    {contact ? `${contact.first_name} ${contact.last_name || ""}` : (L ? "Άγνωστη επαφή" : "Unknown contact")}
                  </div>
                  <div className="text-sm text-slate-500 truncate">
                    {contact?.company_name && <>{contact.company_name} · </>}
                    {c.phone_number_called || contact?.phone || "—"}
                  </div>
                  {c.notes && <div className="mt-1 text-xs text-slate-600 line-clamp-1">{c.notes}</div>}
                </div>
                <div className="text-right text-xs text-slate-500 hidden sm:block">
                  <div className="font-semibold text-slate-700">
                    {c.scheduled_callback_at
                      ? new Date(c.scheduled_callback_at).toLocaleString(L ? "el-GR" : "en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })
                      : new Date(c.date).toLocaleString(L ? "el-GR" : "en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                  </div>
                  <div className="mt-1">{L ? "Προγραμ." : "Scheduled"}</div>
                </div>
                <div className="flex gap-2">
                  {contact && <button onClick={() => showTimeline(contact.id)} className="p-2 text-slate-500 hover:text-[#0052CC] border border-slate-200 rounded-md" data-testid={`today-timeline-${c.id}`} title={L ? "Ιστορικό" : "Timeline"}><History className="w-4 h-4" /></button>}
                  <button
                    data-testid={`today-call-now-${c.id}`}
                    onClick={() => { setPreset({ contact_id: c.contact_id, existing_call_id: c.id }); setOpenLog(true); }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold"
                  >
                    <Phone className="w-4 h-4" /> {L ? "Κλήση" : "Call"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Queue: uncalled contacts */}
      {tab === "queue" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {uncalled.length === 0 ? (
            <EmptyState icon={ListChecks} text={L ? "Έχουν καλεσθεί όλοι οι επαφές σας. Καλή δουλειά!" : "All your contacts have been called. Great job!"} />
          ) : (
            <table className="w-full text-sm" data-testid="uncalled-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {[L ? "Επαφή" : "Contact", L ? "Εταιρεία" : "Company", L ? "Χώρα" : "Country", L ? "Τηλέφωνο" : "Phone", L ? "Ενέργειες" : "Actions"].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {uncalled.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`uncalled-row-${c.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{c.first_name} {c.last_name}</div>
                      <div className="text-xs text-slate-500">{c.email || "—"}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-700">{c.company_name || "—"}</td>
                    <td className="py-3 px-4 text-slate-700">{c.country || "—"}</td>
                    <td className="py-3 px-4 text-slate-700">{c.phone || "—"}</td>
                    <td className="py-3 px-4 text-right">
                      <button
                        data-testid={`uncalled-call-${c.id}`}
                        onClick={() => openLogFor(c.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0052CC] text-white rounded-md text-xs font-semibold"
                      >
                        <Phone className="w-3.5 h-3.5" /> {L ? "Κλήση τώρα" : "Call now"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* History: all calls */}
      {tab === "history" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {calls.length === 0 ? (
            <EmptyState icon={History} text={L ? "Δεν υπάρχει ιστορικό κλήσεων." : "No call history yet."} />
          ) : (
            <table className="w-full text-sm" data-testid="calls-history-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {[L ? "Ημ/νία" : "Date", L ? "Επαφή" : "Contact", L ? "Διάρκεια" : "Duration", L ? "Αποτέλεσμα" : "Outcome", L ? "Κατάσταση" : "Status", L ? "Σημειώσεις" : "Notes", ""].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => {
                  const contact = contactMap[c.contact_id];
                  const O = OUTCOME_STYLES[c.outcome] || OUTCOME_STYLES.no_answer;
                  return (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap">
                        {new Date(c.date).toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="py-3 px-4">
                        <button onClick={() => contact && showTimeline(contact.id)} className="text-left hover:text-[#0052CC]" data-testid={`history-contact-${c.id}`}>
                          <div className="font-medium text-slate-900">{contact ? `${contact.first_name} ${contact.last_name || ""}` : "—"}</div>
                          {contact?.company_name && <div className="text-xs text-slate-500">{contact.company_name}</div>}
                        </button>
                      </td>
                      <td className="py-3 px-4">{c.duration_minutes} min</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded ${O.bg} ${O.text}`}>
                          <O.icon className="w-3 h-3" />
                          {t(`outcome.${c.outcome}`)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${c.status === "completed" ? "bg-emerald-100 text-emerald-700" : c.status === "planned" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                          {c.status === "completed" ? (L ? "Ολοκλ." : "Done") : c.status === "planned" ? (L ? "Προγρ." : "Planned") : c.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 max-w-xs truncate">{c.notes || "—"}</td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <button onClick={() => setEditCall(c)} className="p-1.5 text-slate-500 hover:text-[#0052CC]" data-testid={`call-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {openLog && (
        <CallModal
          contacts={contacts}
          preset={preset}
          onClose={() => { setOpenLog(false); setPreset(null); }}
          onSaved={() => { setOpenLog(false); setPreset(null); load(); }}
          L={L}
          t={t}
        />
      )}

      {editCall && (
        <CallModal
          contacts={contacts}
          existing={editCall}
          onClose={() => setEditCall(null)}
          onSaved={() => { setEditCall(null); load(); }}
          L={L}
          t={t}
        />
      )}

      {timeline && <TimelineModal data={timeline} onClose={() => setTimeline(null)} L={L} t={t} />}
      {timelineContactId && <ContactTimeline contactId={timelineContactId} onClose={() => { setTimelineContactId(null); load(); }} />}
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 truncate">{label}</div>
          <div className="font-heading text-xl font-bold text-slate-900">{value}</div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="py-16 px-6 text-center">
      <Icon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-sm text-slate-500 max-w-md mx-auto">{text}</p>
    </div>
  );
}

function CallModal({ contacts, preset, existing, onClose, onSaved, L, t }) {
  const [form, setForm] = useState(() => {
    if (existing) {
      return {
        contact_id: existing.contact_id || "",
        duration_minutes: existing.duration_minutes || 0,
        outcome: existing.outcome || "no_answer",
        status: existing.status || "completed",
        scheduled_callback_at: existing.scheduled_callback_at || "",
        phone_number_called: existing.phone_number_called || "",
        notes: existing.notes || "",
      };
    }
    return {
      contact_id: preset?.contact_id || "",
      duration_minutes: 0,
      outcome: "no_answer",
      status: "completed",
      scheduled_callback_at: "",
      phone_number_called: "",
      notes: "",
    };
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const isEdit = !!existing;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const payload = {
        ...form,
        duration_minutes: parseInt(form.duration_minutes) || 0,
        scheduled_callback_at: form.scheduled_callback_at || null,
      };
      if (form.outcome === "callback_requested" && !payload.status) payload.status = "planned";
      if (isEdit) {
        await api.patch(`/calls/${existing.id}`, payload);
      } else if (preset?.existing_call_id) {
        await api.patch(`/calls/${preset.existing_call_id}`, payload);
      } else {
        await api.post("/calls", payload);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h3 className="font-heading font-semibold text-slate-900">{isEdit ? (L ? "Επεξεργασία κλήσης" : "Edit call") : (L ? "Καταχώρηση κλήσης" : "Log call")}</h3>
            <div className="text-xs text-slate-500">{L ? "Καταγράψτε αποτέλεσμα και επόμενα βήματα" : "Capture outcome and next steps"}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-4 space-y-3" data-testid="call-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Επαφή" : "Contact"}</label>
            <select data-testid="call-contact" required value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
              <option value="">{L ? "Επιλογή..." : "Select..."}</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ""} {c.company_name ? `· ${c.company_name}` : ""}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Διάρκεια (λεπτά)" : "Duration (min)"}</label>
              <input data-testid="call-duration" type="number" min="0" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Τηλέφωνο που κλήθηκε" : "Number called"}</label>
              <input data-testid="call-phone" type="tel" value={form.phone_number_called} onChange={(e) => setForm({ ...form, phone_number_called: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Αποτέλεσμα" : "Outcome"}</label>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {CALL_OUTCOMES.map((o) => {
                const O = OUTCOME_STYLES[o];
                const active = form.outcome === o;
                return (
                  <button
                    key={o}
                    type="button"
                    data-testid={`call-outcome-${o}`}
                    onClick={() => {
                      setForm({ ...form, outcome: o, status: o === "callback_requested" ? "planned" : "completed" });
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs font-semibold transition-all ${
                      active ? "border-[#0052CC] bg-[#0052CC]/10 text-[#0052CC]" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <O.icon className="w-3.5 h-3.5" />
                    {t(`outcome.${o}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {(form.outcome === "callback_requested" || form.status === "planned") && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-800">
                {L ? "Πότε να ξανακαλέσετε;" : "When to call back?"}
              </label>
              <input
                data-testid="call-callback-at"
                type="datetime-local"
                value={form.scheduled_callback_at ? form.scheduled_callback_at.slice(0, 16) : ""}
                onChange={(e) => setForm({ ...form, scheduled_callback_at: e.target.value })}
                className="mt-1.5 w-full px-3 py-2 border border-amber-300 rounded-md text-sm bg-white"
              />
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Τι ειπώθηκε / σημειώσεις" : "What was said / notes"}</label>
            <textarea
              data-testid="call-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              placeholder={L ? "π.χ. Είδαμε ενδιαφέρον για olive oil, ζητάει τιμοκατάλογο..." : "e.g. Showed interest in olive oil, requested price list..."}
              className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
            />
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{L ? "Ακύρωση" : "Cancel"}</button>
            <button type="submit" disabled={saving} data-testid="call-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold disabled:opacity-50">
              {saving ? "..." : (L ? "Αποθήκευση" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TimelineModal({ data, onClose, L, t }) {
  const { contact, calls, opportunities } = data;
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="timeline-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="font-heading text-xl font-bold text-slate-900">{contact.first_name} {contact.last_name}</h3>
            <div className="text-sm text-slate-500 mt-0.5">{contact.company_name || ""}{contact.country ? ` · ${contact.country}` : ""}</div>
            <div className="text-xs text-slate-400 mt-1">{contact.email} {contact.phone ? `· ${contact.phone}` : ""}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">{L ? "Ιστορικό επικοινωνίας" : "Communication timeline"}</div>
          {calls.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">{L ? "Καμία κλήση ακόμη." : "No calls yet."}</div>
          ) : (
            <div className="space-y-3 relative">
              <div className="absolute left-5 top-2 bottom-2 w-px bg-slate-200" />
              {calls.map((c) => {
                const O = OUTCOME_STYLES[c.outcome] || OUTCOME_STYLES.no_answer;
                return (
                  <div key={c.id} className="relative pl-12">
                    <div className={`absolute left-2 top-1 w-7 h-7 rounded-full flex items-center justify-center ${O.bg}`}>
                      <O.icon className={`w-3.5 h-3.5 ${O.text}`} />
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${O.bg} ${O.text}`}>{t(`outcome.${c.outcome}`)}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(c.date).toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          {c.duration_minutes > 0 && <> · {c.duration_minutes} min</>}
                        </span>
                      </div>
                      {c.notes && <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.notes}</p>}
                      {c.scheduled_callback_at && (
                        <div className="mt-2 text-xs text-amber-800 bg-amber-100 inline-flex items-center gap-1 px-2 py-1 rounded">
                          <CalendarClock className="w-3 h-3" />
                          {L ? "Επόμενη κλήση:" : "Next callback:"} {new Date(c.scheduled_callback_at).toLocaleString(L ? "el-GR" : "en-GB")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {opportunities.length > 0 && (
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">{L ? "Ευκαιρίες στη διοχέτευση" : "Pipeline opportunities"}</div>
              <div className="space-y-2">
                {opportunities.map((o) => (
                  <div key={o.id} className="bg-white border border-slate-200 rounded-md p-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900 text-sm">{o.title}</div>
                      <div className="text-xs text-slate-500">{o.lead_source || "—"}</div>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#0052CC]/10 text-[#0052CC]">{t(`stage.${o.stage}`)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


